import type { MouseEvent as ReactMouseEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AppstoreOutlined, LockOutlined } from '../../components/icons'
import { Card } from '../../components/ui/Card'
import { Chip } from '../../components/ui/Chip'
import { displayNameFor } from '../../lib/fixtures/sourceable-lookup'
import { formatMoney } from '../cart/cart-money'
import { PartThumbnail } from './PartThumbnail'
import { RfqEtaChip } from './RfqEtaChip'
import { StatusStepper } from './StatusStepper'
import {
  RFQ_BUNDLED_NOTE,
  RFQ_STATUS_CHIP,
  etaForRfq,
  quoteHasMultipleVariants,
  rfqPartsMetaLineBody,
  shipByForQuote,
  showsInProgressStepper,
  subtotalForQuote,
} from './domain'
import type { Quote, Rfq, RfqLineView, RfqTabId } from './domain'
import { useRfqQuote } from './use-rfq-quote'

/** UID-294 (§7 body row) — the per-line thumbnail stack shows at most this
 * many real tiles before folding the remainder into a "+N" overflow tile,
 * matching the design's own cap (`Fictiv Platform.dc.html` ~L9842:
 * `partsSrc.slice(0,4)` / `hasExtra: partsSrc.length>4`). */
const THUMB_MAX = 4

export interface RfqListRowProps {
  readonly rfq: Rfq
  /** The tab this row is currently rendered under — carried forward so
   * "← Back" on the detail page can restore the exact tab/scroll position
   * (ADR-0056 Decision 1). */
  readonly activeTab: RfqTabId
}

/**
 * UID-135 (ADR-0056 Decision 4) — the compact list row that REPLACES
 * `RfqCard` as `RfqListPage`'s per-row rendering. `RfqCard` itself is
 * reused, UNCHANGED, as the new `/rfq/:rfqId` detail page's body.
 *
 * UID-294 (§7) — restructured to the design's per-category reference card
 * (`Fictiv Platform.dc.html` ~L3448-3501): a top meta row ("Submitted
 * <date>" · an optional bold brand-navy "Quoted <date>" · the inline "View
 * RFQ →" nav link · a trailing multi-part/Bundled/status-chip cluster) over
 * a body row (an overlapping per-line thumbnail stack, name, meta,
 * project/ship-by, and — once a quote has loaded — a right-aligned price).
 * A behavior-PRESERVING restructure of a shipped component (legacy-
 * refactoring discipline): the nav/scroll-restore/Bundled-badge mechanics
 * below are UNCHANGED from the prior markup, only their visual position
 * moved; `RfqListRow.test.tsx`'s pre-existing suite pins all three and
 * stayed green throughout. One disclosed, deliberate NON-match against the
 * literal design (flagged here rather than guessed past), plus one REVERSED
 * one:
 * - No "+ Add to project" link is rendered when `rfq.projectName` is unset
 *   — `RfqCard.test.tsx`'s "Descope guard" documents ADR-0055 §D1: there is
 *   no RFQ↔workspace contract link either direction, so this stays a dead
 *   affordance rather than a real one; the project name still renders when
 *   present — reaffirmed by ADR-0112 D5.
 * - The design shows "{projectName} · Ship by {date}" unconditionally,
 *   falling back to "Ship by N/A" pre-quote (`Fictiv Platform.dc.html`
 *   ~L3485's ungated `<span>Ship by {{ r.shipByLabel }}</span>`, fed by
 *   ~L9823's `shipByLabel:_shipDate?dcFmt(_shipDate):'N/A'`). This build
 *   USED to gate that line behind `quoteLoaded` to avoid a "premature
 *   Ship by N/A". UID-327/328 (ADR-0112 D5) reverses that for EXPIRED
 *   ONLY: an expired RFQ will never ship, so "N/A" is the accurate final
 *   answer rather than a premature one, and the "Expired" chip alongside
 *   it prevents any reading of it as a pending commitment — which is also
 *   what ADR-0104 D4 §7's body composition already specified.
 *   `requested`/`quoting` KEEP the omission: neither of those clauses
 *   holds for an in-progress request, where "Ship by N/A" reads as a
 *   missing commitment instead of an absent one. So this remains a
 *   PARTIAL, deliberate non-match with the literal design — a product
 *   ruling (human, 2026-08-27) that outranks design fidelity here because
 *   the prototype has no notion of that misreading. Do not "finish" the
 *   reversal by ungating the other two statuses without a product
 *   decision. Only the PRICE block stays purely quote-gated (the design's
 *   `headerPrice`/`headerTotal` are `isReturned`-only, ~L9880) —
 *   ADR-0104 D4 §7's "Expired = muted, no price" is unaffected.
 *
 * UID-289 (ADR-0056 amendment, 2026-08-25) — the whole-row navigation
 * mechanism is a REAL `<a href>` (via react-router `<Link>`), not a
 * `role="link"` div. The link-overlay ("stretched link") technique keeps
 * exactly one real interactive nav element AND keeps the Bundled badge
 * independently operable, with NO ancestor/descendant relationship between
 * them at all (so there is nothing for axe's `nested-interactive` rule to
 * even consider):
 *
 * - The `<Link>` lives in the row's top meta row, visually as the small
 *   "View RFQ →" affordance, but is stretched to cover the WHOLE card via
 *   `.rfq-list-row-view-link::after { position: absolute; inset: 0 }`
 *   (rfq.css) — a real `<a>`, so keyboard Enter, middle-click/open-in-new-
 *   tab, and native `role="link"` semantics all come for free, no
 *   `onClick`-driven navigation needed for the forward hop itself. Its
 *   position within the row is purely visual — the `::after` overlay
 *   resolves against `.rfq-list-row`'s own `position: relative`
 *   regardless of where the anchor itself sits in the flow.
 * - The Bundled badge is a `<button>` rendered in the row's top meta row —
 *   a sibling subtree of the `<Link>`, never its descendant — so it stays
 *   independently clickable/focusable. Its wrapper (`.rfq-card-bundled-
 *   wrap`) is given a higher `z-index` than the stretched overlay so a
 *   pointer click that lands visually on the badge is routed to the badge,
 *   not swallowed by the overlay underneath it.
 * - `handleLinkClick` still snapshots the current tab's scroll position
 *   onto the CURRENT `/rfq` history entry (a `replace` navigate) — the
 *   ADR-0056 Decision 1 back-restoration contract — before letting the
 *   `<Link>`'s own default navigation to the detail route proceed.
 *
 * Reuses the SAME `domain.ts` helpers (`shipByForQuote`,
 * `subtotalForQuote`, `quoteHasMultipleVariants`, `RFQ_STATUS_CHIP`,
 * `RFQ_BUNDLED_NOTE`, `rfqPartsMetaLineBody`, `etaForRfq`,
 * `showsInProgressStepper`) and the extracted `useRfqQuote` hook `RfqCard`
 * also uses — no duplicated derivation logic, only presentational markup
 * differs from the card header. The Bundled badge's disclosure widget is a
 * small, self-contained a11y pattern duplicated from `RfqCard.tsx` rather
 * than imported, so `RfqCard.tsx`'s only edit for this ticket stays the
 * `useRfqQuote` import line (see that file's doc comment).
 */
export function RfqListRow({ rfq, activeTab }: RfqListRowProps) {
  const navigate = useNavigate()
  const chip = RFQ_STATUS_CHIP[rfq.status]
  const showsQuote = (rfq.status === 'quoted' || rfq.status === 'accepted') && Boolean(rfq.quoteId)
  const { quote, quoteLoadState } = useRfqQuote(rfq.id, showsQuote)
  const detailPath = `/rfq/${rfq.id}`
  // The `as readonly RfqLineView[]` cast — see `RfqCard.tsx`'s
  // `requirementsBySourceableId` doc comment for the full explanation of why
  // plain property access on `rfq.lines` doesn't expose the additive
  // `RfqLineView` fields (here, `part`).
  const lines = rfq.lines as readonly RfqLineView[]
  const firstLine = lines[0]
  const partName = firstLine ? (firstLine.part?.name ?? displayNameFor(firstLine.sourceable)) : null
  const partCountLabel = `${lines.length} ${lines.length === 1 ? 'part' : 'parts'}`
  // UID-294 (§7 top-meta multi-part badge) — the design's own `isMulti`
  // (`r.parts.length>1`), not `quoteHasMultipleVariants` (that's a QUOTE
  // pricing-region count, a different concept keyed off the Quote, not the
  // RFQ's own line count).
  const hasMultipleParts = lines.length > 1
  // UID-294 (§7 top-meta row) — "Submitted"/"Quoted" now render as two
  // independently-styled segments (the "Quoted" one bold brand-navy, only
  // once present) rather than the single joined string
  // `rfqSubmittedQuotedLabel` still produces for `RfqDetailPage.tsx`
  // (untouched, out of this ticket's scope) — computed inline rather than
  // via a new domain.ts export, since domain.ts is not part of this
  // ticket's file scope.
  const submittedDate = rfq.createdAt ? rfq.createdAt.slice(0, 10) : null
  const quotedDate = rfq.quotedAt ? rfq.quotedAt.slice(0, 10) : null
  // Gates the price block, and (with `showsProjectShip` below) the
  // project/ship-by line — see the component doc comment's second disclosed
  // non-match.
  const quoteLoaded = showsQuote && quote && quoteLoadState === 'loaded' ? quote : undefined
  // UID-327/328 (ADR-0112 D5, final) — the project/ship-by line renders for
  // an EXPIRED row (where "Ship by N/A" is the accurate final answer) and for
  // a quoted/accepted row (where a real date exists), but NOT for
  // `requested`/`quoting`: "Ship by N/A" on a request that has not been
  // quoted yet reads as a missing commitment rather than an absent one. That
  // narrower scope is a deliberate product ruling, NOT the literal design —
  // see the doc comment above and ADR-0112 D5.
  const showsProjectShip = rfq.status === 'expired' || Boolean(quoteLoaded)

  // ADR-0056 Decision 1 (router-state-based restoration, no `?tab=` param):
  // before leaving the list, snapshot this tab's scroll offset onto the
  // CURRENT `/rfq` history entry (a `replace` navigate) so a later
  // `navigate(-1)` from the detail page's "← Back" pops right back to it
  // with `location.state` intact — plus a `sessionStorage` backstop for a
  // hard reload mid-flow, per the ADR. Runs on the real `<Link>`'s `onClick`
  // (UID-289) — it does NOT call `event.preventDefault()`, so the anchor's
  // own default navigation to `detailPath` still fires immediately after.
  function handleLinkClick(_event: ReactMouseEvent<HTMLAnchorElement>) {
    const fromScrollY = window.scrollY
    try {
      sessionStorage.setItem(`rfq:list:scroll:${activeTab}`, String(fromScrollY))
      sessionStorage.setItem('rfq:list:activeTab', activeTab)
    } catch {
      // sessionStorage unavailable (private mode etc.) — router state below
      // still carries the same values for the common case.
    }
    navigate('/rfq', {
      replace: true,
      state: { fromTab: activeTab, fromScrollY, fromRfqId: rfq.id },
    })
  }

  return (
    <Card className={`rfq-list-row rfq-list-row-${rfq.status}`} interactive>
      {/* UID-294 (§7 top meta row) — "Submitted <date>" · optional bold
       * "Quoted <date>" · the "View RFQ →" nav link · a trailing
       * multi-part/Bundled/status-chip cluster. The design's PO-reference
       * slot that used to render here (`rfq.poReference`) is REMOVED — it
       * is not present in `Fictiv Platform.dc.html`'s reference card;
       * `RfqCard.tsx`'s detail-page header keeps its own PO display
       * unchanged (`.rfq-card-po`/`.rfq-card-header-badges`, untouched). */}
      <div className="rfq-list-row-topmeta">
        {submittedDate && (
          <span className="rfq-list-row-submitted-date">Submitted {submittedDate}</span>
        )}
        {quotedDate && <span className="rfq-list-row-quoted-date">· Quoted {quotedDate}</span>}
        {/* UID-289 — the ONE real navigation control on the row: a real
         * `<a href>` (react-router `Link`), visually stretched to cover the
         * whole card via `::after` (rfq.css) — see the component doc
         * comment above. `id` is looked up by `RfqListPage`'s §1.3
         * focus-on-back restoration. */}
        <Link
          to={detailPath}
          id={`rfq-view-link-${rfq.id}`}
          className="rfq-list-row-view-link"
          aria-label={`View RFQ ${rfq.id} — ${chip.label}`}
          onClick={handleLinkClick}
        >
          View RFQ <ViewRfqChevron />
        </Link>
        <div className="rfq-list-row-topmeta-trailing">
          {hasMultipleParts && (
            <span className="rfq-list-row-multi-badge">
              <AppstoreOutlined aria-hidden="true" />
              {lines.length} parts
            </span>
          )}
          {rfq.bundled === true && <RowBundledBadge />}
          <Chip className="rfq-chip" tone={chip.tone}>
            {chip.label}
          </Chip>
        </div>
      </div>

      <div className="rfq-card-body">
        {/* UID-294 (§7 body row) — one overlapping thumbnail per line
         * (`THUMB_MAX` cap + "+N" overflow), replacing the old
         * first-line-only thumbnail. */}
        <div className="rfq-list-row-thumbs">
          {lines.slice(0, THUMB_MAX).map((line, index) => (
            <PartThumbnail
              // biome-ignore lint/suspicious/noArrayIndexKey: a wire LineItem has no stable per-line id (domain.ts's RfqLineView doc comment) — this is a decorative, never-reordered stack for one render of one RFQ.
              key={index}
              className="rfq-part-thumb rfq-list-row-thumb"
              url={line.part?.thumbnailRef ?? null}
            />
          ))}
          {lines.length > THUMB_MAX && (
            <span className="rfq-list-row-thumb-extra" aria-hidden="true">
              +{lines.length - THUMB_MAX}
            </span>
          )}
        </div>
        <div className="rfq-card-lines-block">
          {partName && (
            <p className="rfq-list-row-name" title={partName}>
              {partName}
            </p>
          )}
          {lines.length > 0 && (
            <p className="rfq-list-row-meta caption-text">
              <strong>{partCountLabel}</strong> · {rfqPartsMetaLineBody(lines)}
            </p>
          )}
          {/* UID-327/328 (ADR-0112 D5, final) — scoped to EXPIRED plus the
           * pre-existing quoted/accepted path, NOT ungated for every status:
           * an expired row calls this with `undefined` and gets the honest
           * "Ship by N/A", a quoted/accepted row gets a real date, and
           * `requested`/`quoting` render no line at all (the pre-existing
           * omission stands for those two). See the component doc comment
           * above for why the literal design is not followed here. */}
          {showsProjectShip && (
            <RowProjectShip quote={quoteLoaded} projectName={rfq.projectName ?? null} />
          )}
        </div>
        {quoteLoaded && <RowPriceBlock quote={quoteLoaded} />}
      </div>

      {/* UID-193 — a decorative-only reinforcement of the status already
       * named by the Chip above: the whole subtree is pulled out of the
       * accessible tree (`aria-hidden`) so a screen reader never announces a
       * redundant 3-step status per row (WCAG 1.4.1 — the Chip stays the
       * single SR status source; ORCHESTRATOR RULING, UID-193). UID-294
       * (§7 "Requested/in-review" category) adds the real, SR-visible
       * "Quote in progress · estimated return <eta>" line alongside it,
       * mirroring `RfqCard.tsx`'s own detail-page pairing of the same two
       * pieces (`showsInProgressStepper` gates both identically there). */}
      {showsInProgressStepper(rfq.status) && (
        <>
          <div aria-hidden="true">
            <StatusStepper status={rfq.status} variant="compact" />
          </div>
          <RfqEtaChip eta={etaForRfq(rfq.submittedAt ?? rfq.createdAt ?? '')} />
        </>
      )}
    </Card>
  )
}

/** UID-289 — the "View RFQ" chevron-right glyph, matching the design's own
 * inline SVG exactly (`Fictiv Platform.dc.html` ~L3468: 15×15, stroke-width
 * 2.2, `M9 6l6 6-6 6`) rather than the prior ASCII `→`. No `chevron-right`
 * entry exists in the shared `IconName` registry (`icon-registry.ts`) —
 * mirrors `RfqCard.tsx`'s own `RetryGlyph` inline-SVG precedent rather than
 * widening that shared union for one glyph. Decorative — the Link's own
 * visible "View RFQ" text plus its `aria-label` already carry the meaning.
 */
function ViewRfqChevron() {
  return (
    <svg
      width={15}
      height={15}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  )
}

/** UID-294 (§7 body row) — "{project name} · Ship by {date}" (or just
 * "Ship by {date}" absent a project), rendered in the LEFT block under the
 * name/meta lines, matching the design's `{{r.wsName}} · Ship by
 * {{r.shipByLabel}}` line (`Fictiv Platform.dc.html` ~L3483). Split out of
 * the old combined `RowQuoteSummary` (below, now `RowPriceBlock`) now that
 * the design puts this line on the LEFT and the price on the RIGHT, as two
 * independent pieces rather than one right-aligned stack. Deliberately
 * never renders a "+ Add to project" affordance for a missing project name
 * — see the component doc comment's first disclosed non-match
 * (ADR-0055 §D1 descope).
 *
 * UID-327/328 (ADR-0112 D5, final) — `quote` is now `Quote | undefined` so an
 * EXPIRED row can mount this without one and get the honest "Ship by N/A"
 * (`shipByForQuote` already returns `null` absent a quote, and the `?? 'N/A'`
 * below is unchanged). The caller decides WHETHER to mount it
 * (`showsProjectShip` — expired or quoted/accepted only, never
 * `requested`/`quoting`); this component does not branch on status itself. */
function RowProjectShip({
  quote,
  projectName,
}: {
  readonly quote: Quote | undefined
  readonly projectName: string | null
}) {
  const shipBy = quote ? shipByForQuote(quote) : null
  return (
    <p className="rfq-list-row-project-ship caption-text">
      {projectName && <span className="rfq-card-project">{projectName} · </span>}
      <span className="rfq-card-shipby">Ship by {shipBy ?? 'N/A'}</span>
    </p>
  )
}

/** UID-294 (§7 "Quoted/returned" category), amended by UID-607 — the
 * right-aligned total-price block, split out of the old combined
 * `RowQuoteSummary` (see `RowProjectShip` above). Renders nothing when the
 * quote carries no addable line to sum.
 *
 * UID-607: shows the quote's TOTAL (`subtotalForQuote`, the cheapest
 * variant's real summed price), not a per-unit rate — this card used to
 * show `bestOptionPriceForQuote` with a "/ea" suffix, which read as a unit
 * price when the RFQ detail page's own summary shows a dollar total for
 * the same quote. */
function RowPriceBlock({ quote }: { readonly quote: Quote }) {
  const total = subtotalForQuote(quote)
  const showQualifier = quoteHasMultipleVariants(quote)
  if (!total) return null
  return (
    <div className="rfq-card-summary">
      <span
        className="rfq-card-price"
        aria-label={`${formatMoney(total.amount, total.currency)} total${
          showQualifier ? ', best option' : ''
        }`}
      >
        <span className="rfq-card-price-amount">{formatMoney(total.amount, total.currency)}</span>
        {showQualifier && <span className="rfq-card-price-qualifier">best option</span>}
      </span>
    </div>
  )
}

/** Presentational duplicate of `RfqCard.tsx`'s `BundledBadge` — same
 * keyboard-operable disclosure button pattern (WCAG 1.4.13/2.1.1/4.1.2),
 * duplicated rather than imported so `RfqCard.tsx` stays untouched beyond
 * its `useRfqQuote` import line. */
function RowBundledBadge() {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const noteId = useId()

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (!popoverRef.current?.contains(target) && !buttonRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  return (
    <span className="rfq-card-bundled-wrap">
      <button
        type="button"
        ref={buttonRef}
        className="rfq-card-bundled-badge"
        aria-label="Bundled — why this quote is combined"
        aria-expanded={open}
        aria-controls={open ? noteId : undefined}
        title="Bundled — why this quote is combined"
        onClick={(event) => {
          // UID-289: the badge is a sibling of the row's real nav `<Link>`
          // (never its descendant, see the component doc comment) — there
          // is no row-level `onClick` left to bubble into. `stopPropagation`
          // is kept defensively (harmless) in case a future ancestor ever
          // adds one.
          event.stopPropagation()
          setOpen((current) => !current)
        }}
      >
        <LockOutlined aria-hidden="true" />
        <span>Bundled</span>
      </button>
      {open && (
        <div ref={popoverRef} id={noteId} className="rfq-card-bundled-popover">
          {RFQ_BUNDLED_NOTE}
        </div>
      )}
    </span>
  )
}
